import { normalize } from '../search/searchEngine';
import { writtenSteps } from './guides';

/**
 * What «اسأل مشكاة» knows, and how it answers with no connection.
 *
 * knowledgeFor() is the text sent with every question: the pages this
 * role has and its guides, written out — the same words the help page
 * shows, so the assistant can only point at what really exists.
 *
 * matchGuides() is the offline answer (and the answer while the server
 * has no AI key): the guides whose words are in the question.
 */

/** One line on each page, for «where do I find…». */
const PAGE_NOTES = {
  'teacher-home': 'صفحة المعلم الرئيسية: شعبه، تذكير التفقّد، أخذ التفقّد، الطلاب الذين يحتاجون متابعة، الامتحانات غير المنشورة',
  'student-directory': 'ملف كل طالب: بياناته، حضوره، علاماته، قيوده، أقساطه، طباعة وثائقه. وفيها «إضبارة جديدة» لتسجيل طالب و«الإضبارات»',
  monitor: 'لوحة المراقبة: من أخذ التفقّد اليوم ومن تأخّر، الغياب، وآخر ما تغيّر في النظام',
  students: 'جدول الطلاب: تعديل، نقل إلى شعبة أو صف آخر، أرشفة، والاستيراد من Excel',
  'student-import': 'استيراد الطلاب أو تحديثهم من ملف Excel',
  families: 'العائلات: الأب والأم وهواتفهم وأبناؤهم في المدرسة',
  enrollments: 'القيود: تسجيل طالب ليس في شعبة داخل شعبة',
  attendance: 'التفقّد: سجلات الحضور لكل شعبة ويوم، وتعديل حالة طالب',
  exams: 'الامتحانات: إنشاء امتحان، رصد العلامات، النشر',
  teachers: 'المعلمون: إضافة معلم، هاتفه (به يدخل)، إيقاف حسابه',
  assignments: 'تدريس المواد: توكيل معلم بمادة في شعبة، وتبديل المعلم',
  sections: 'الشعب: إنشاء شعبة، الصف والفوج، معلم الصف للابتدائي',
  grades: 'الصفوف: ترتيبها و«نمط ابتدائي»',
  subjects: 'المواد: قائمة مواد المدرسة',
  batches: 'الأفواج: صباحي ومسائي',
  transportation: 'المواصلات: الباصات والسائقون والأجرة الشهرية',
  payments: 'الأقساط: سجل الدفعات، المتأخرون عن الشهر الحالي، تسجيل دفعة',
  users: 'المستخدمون: حسابات الإدارة والموجهين، رمز الدخول، إيقاف حساب، سجل الدخول',
  'audit-log': 'سجل العمليات: من غيّر ماذا ومتى',
  backups: 'النسخ الاحتياطي واسترجاعه، و«البدء من جديد»',
  services: 'الخدمات المرتبطة: رصيد الرسائل واستهلاك الخادم',
  notifications: 'الإشعارات: إرسال إشعار للطلاب والأهل والمعلمين، ومن قرأه',
  help: 'دليل الاستخدام: كل الأدلة خطوة بخطوة مع «ورّيني»',
  about: 'حول النظام: بيانات المؤسسة والعام الدراسي',
};

export function knowledgeFor(guides, nav) {
  const pages = nav.map((p) => `- «${p.label}»${PAGE_NOTES[p.id] ? `: ${PAGE_NOTES[p.id]}` : ''}`).join('\n');
  const body = guides.map((x) => {
    const steps = writtenSteps(x).map((s, i) => `${i + 1}. ${s.title}: ${s.text}`).join('\n');
    const notes = (x.notes || []).map((n) => `- ${n}`).join('\n');
    return `### ${x.id} — ${x.title}\n${x.summary}\n${steps}${notes ? `\nملاحظات:\n${notes}` : ''}`;
  }).join('\n\n');
  return `## خريطة الصفحات\n${pages}\n\n## دليل الاستخدام (المعرّف — العنوان)\n${body}`;
}

// Words that say nothing about WHAT is asked.
const STOP = new Set(normalize(
  'كيف ماذا ما هل في على من الى إلى عن او أو ان أن لا يا انا أنا اريد أريد بدي ممكن لو شو يمكن لي الي '
  + 'هذا هذه ذلك التي الذي عند مع ثم بعد قبل كل بس لازم شلون وين أين اين متى لماذا ليش اعمل أعمل بعمل نعمل',
).split(' '));

/** The guides whose words are in the question, best first (at most `max`). */
export function matchGuides(guides, question, max = 3) {
  const tokens = normalize(question).split(' ').filter((t) => t.length > 1 && !STOP.has(t));
  if (!tokens.length) return [];
  return guides
    .map((x) => {
      const title = ` ${normalize(x.title)} `;
      const words = ` ${normalize(x.words)} `;
      const rest = ` ${normalize([x.summary, ...writtenSteps(x).map((s) => s.title)].join(' '))} `;
      let score = 0;
      tokens.forEach((t) => {
        if (title.includes(t)) score += 3;
        else if (words.includes(t)) score += 2;
        else if (rest.includes(t)) score += 1;
      });
      return { x, score };
    })
    .filter((r) => r.score > 1)
    .sort((a, b) => b.score - a.score)
    .slice(0, max)
    .map((r) => r.x);
}

/** First questions to offer, per role. */
export const SUGGESTIONS = {
  admin: [
    'مين أكثر الطلاب غياباً هالشهر؟',
    'مين ما أخد تفقّد اليوم؟',
    'مين متأخر بالدفع؟',
    'كيف أجهّز الصفوف الابتدائية؟',
  ],
  register: [
    'مين غايب اليوم؟',
    'مين متأخر بالدفع؟',
    'مين أكثر الطلاب غياباً هالأسبوع؟',
    'كيف أسجّل طالباً جديداً في صفه؟',
  ],
  teacher: [
    'مين غايب اليوم من طلابي؟',
    'مين أكثر طلابي غياباً هالشهر؟',
    'كيف آخذ التفقّد؟',
    'كيف أنشر العلامات للأهل؟',
  ],
};

/** «ماذا يستطيع مشكاة؟» — the side of «مشكاة الآلي», per role. */
const STAFF = ['admin', 'register', 'teacher'];
const OFFICE = ['admin', 'register'];
export const CAPABILITIES = [
  {
    key: 'attendance', tone: 'emerald', title: 'الحضور والغياب', roles: STAFF,
    examples: ['مين غايب اليوم؟', 'مين متأخر اليوم؟', 'أكثر الطلاب غياباً هالشهر', 'فلان حضر يوم الأربعاء الماضي؟', 'غياب فلان هالشهر'],
  },
  {
    key: 'registers', tone: 'teal', title: 'التفقّد والمعلمون', roles: OFFICE,
    examples: ['أرقام أهالي الغايبين اليوم بالسابع', 'مين الشعب يلي أخدت تفقّد اليوم؟', 'مين ما أخد تفقّد اليوم؟', 'مين أستاذ مقصّر بالتفقّد هالأسبوع؟'],
  },
  {
    key: 'registers-t', tone: 'teal', title: 'تفقّد شعبي', roles: ['teacher'],
    examples: ['أي شعبة من شعبي ما انأخذ تفقّدها اليوم؟'],
  },
  {
    key: 'marks', tone: 'gold', title: 'العلامات', roles: STAFF,
    examples: ['كيف كانت نتائج امتحانات الرياضيات؟', 'علامات فلان بالرياضيات', 'الطلاب يلي علاماتهم تحت 40', 'جهّزلي للطباعة العلامات تحت 50 بالسابع'],
  },
  {
    key: 'money', tone: 'rose', title: 'الأقساط', roles: ['admin'],
    examples: ['كم دفعوا طلاب البكلوريا هالشهر؟', 'مين متأخر بالدفع؟', 'قديش باقي على فلان؟'],
  },
  {
    key: 'students', tone: 'sky', title: 'الطلاب والشعب', roles: STAFF,
    examples: ['مين الطلاب الساكنين بالحارة الشمالية؟', 'كم طالب بالثامن؟', 'إيمتى سجّل فلان؟ وشو رقم أبوه؟', 'كم طالب بكل شعبة؟'],
  },
  {
    key: 'howto', tone: 'violet', title: 'طريقة الاستخدام', roles: STAFF,
    examples: ['كيف فيك تساعدني؟', 'كيف أنشئ امتحاناً؟', 'كيف أرسل إشعاراً للأهل؟'],
  },
];
